import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import { Button } from '../../ui/Button/Button'
import { Card } from '../../ui/Card/Card'
import { Spinner } from '../../ui/Spinner/Spinner'
import { StatePanel } from '../../ui/StatePanel/StatePanel'
import { useAuth } from '../auth/AuthProvider'
import type { DashboardUser } from '../auth/auth-adapter'
import {
  getOnboardingApiClient,
  OnboardingApiError,
  type OnboardingApiClient,
} from './onboarding-api'
import {
  communicationsPatchSchema,
  type CommunicationsResponse,
} from './onboarding-communications-contracts'
import {
  onboardingCommunicationsQueryOptions,
  onboardingStatusQueryKey,
  onboardingStatusQueryOptions,
} from './onboarding-query'
import { destinationPath, routeOnboardingResponse } from './onboarding-routing'
import styles from './CommunicationsPage.module.css'

type Feedback =
  'saved' | 'conflict' | 'invalid' | 'error' | 'complete-error' | null
type Errors = { areaCode?: string }

function statusLabel(value: string): string {
  return value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function formatPhoneNumber(value: string): string {
  const match = /^\+1([0-9]{3})([0-9]{3})([0-9]{4})$/.exec(value)
  return match ? `(${match[1]}) ${match[2]}-${match[3]}` : value
}

function assignmentStatusCopy(
  status: CommunicationsResponse['assignment']['status']
): string {
  const labels: Record<typeof status, string> = {
    UNASSIGNED: 'Not assigned yet',
    RESERVED: 'Number reserved',
    ASSIGNED: 'Number assigned',
    PROVISIONING: 'Setup in progress',
    ACTIVE: 'Active',
    RETIRED: 'No longer active',
    ERROR: 'Setup needs attention',
    RECONCILIATION_REQUIRED: 'Setup needs review',
  }
  return labels[status]
}

function approvalCopy(status: CommunicationsResponse['smsApproval']['status']) {
  const labels: Record<typeof status, string> = {
    UNKNOWN: 'Verification status not available',
    NOT_REQUIRED: 'No additional SMS verification required',
    PENDING: 'SMS verification pending',
    APPROVED: 'SMS verification approved',
    REJECTED: 'SMS verification was not approved',
    REMEDIATION_REQUIRED: 'SMS setup needs attention',
  }
  return labels[status]
}

function provisioningCopy(
  status: CommunicationsResponse['provisioning']['status']
) {
  if (status === 'SUCCEEDED') return 'Provisioning completed'
  if (status === 'NOT_STARTED') return 'Setup not started'
  if (['PENDING', 'LEASED', 'DISPATCHING'].includes(status)) {
    return 'Setup pending'
  }
  if (status === 'CANCELLED') return 'Setup cancelled'
  if (status === 'FAILED_RETRYABLE') return 'Setup will need to be retried'
  if (status === 'RECONCILIATION_REQUIRED') return 'Setup needs review'
  return 'Setup could not be completed'
}

export function CommunicationsPage({
  client = getOnboardingApiClient(),
}: {
  client?: OnboardingApiClient
}) {
  const auth = useAuth()
  if (auth.status !== 'authenticated') {
    return <Spinner label="Restoring your setup" />
  }
  return (
    <AuthenticatedCommunicationsPage
      key={auth.user.uid}
      client={client}
      user={auth.user}
    />
  )
}

function AuthenticatedCommunicationsPage({
  client,
  user,
}: {
  client: OnboardingApiClient
  user: DashboardUser
}) {
  const auth = useAuth()
  const location = useLocation()
  const statusQuery = useQuery(onboardingStatusQueryOptions(client, user))

  if (statusQuery.isPending) return <Spinner label="Checking your setup" />
  if (statusQuery.isError) {
    if (
      statusQuery.error instanceof OnboardingApiError &&
      statusQuery.error.code === 'unauthorized'
    ) {
      return (
        <Navigate
          to="/login"
          replace
          state={{ from: location.pathname, reauthenticate: true }}
        />
      )
    }
    return (
      <div className={styles.page}>
        <StatePanel
          kind="error"
          title="Communications setup could not be loaded"
          description="Retry to load your authoritative onboarding status."
        />
        <div className={styles.actions}>
          <Button type="button" onClick={() => void statusQuery.refetch()}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  const destination = routeOnboardingResponse(statusQuery.data)
  if (destination !== 'communications') {
    return <Navigate to={destinationPath(destination)} replace />
  }
  return (
    <CommunicationsEditor client={client} user={user} onSignOut={auth.logout} />
  )
}

function CommunicationsEditor({
  client,
  user,
  onSignOut,
}: {
  client: OnboardingApiClient
  user: DashboardUser
  onSignOut: () => Promise<void>
}) {
  const queryClient = useQueryClient()
  const location = useLocation()
  const navigate = useNavigate()
  const query = useQuery(onboardingCommunicationsQueryOptions(client, user))
  const submissionStarted = useRef(false)
  const [initializedRevision, setInitializedRevision] = useState<number | null>(
    null
  )
  const [sms, setSms] = useState(false)
  const [voice, setVoice] = useState(false)
  const [areaCode, setAreaCode] = useState('')
  const [working, setWorking] = useState(false)
  const [feedback, setFeedback] = useState<Feedback>(null)
  const [errors, setErrors] = useState<Errors>({})

  function applyResponse(response: CommunicationsResponse) {
    setInitializedRevision(response.revision)
    setSms(response.preferences.sms)
    setVoice(response.preferences.voice)
    setAreaCode(response.preferences.areaCode ?? '')
    setErrors({})
  }

  useEffect(() => {
    if (query.data && query.data.revision !== initializedRevision) {
      applyResponse(query.data)
    }
  })

  if (query.isPending) return <Spinner label="Loading communications setup" />
  if (query.isError) {
    if (
      query.error instanceof OnboardingApiError &&
      query.error.code === 'unauthorized'
    ) {
      return (
        <Navigate
          to="/login"
          replace
          state={{ from: location.pathname, reauthenticate: true }}
        />
      )
    }
    return (
      <div className={styles.page}>
        <StatePanel
          kind="error"
          title="Communications setup could not be loaded"
          description="Retry to restore the latest saved communications choices."
        />
        <div className={styles.actions}>
          <Button type="button" onClick={() => void query.refetch()}>
            Retry
          </Button>
        </div>
      </div>
    )
  }
  if (initializedRevision === null) {
    return <Spinner label="Restoring communications setup" />
  }

  const smsEnabled = query.data.authorizedCapabilities.sms === 'ENABLED'
  const voiceEnabled = query.data.authorizedCapabilities.voice === 'ENABLED'
  const activeRevision = query.data.revision
  const savedPreferences = query.data.preferences
  const isDirty =
    sms !== savedPreferences.sms ||
    voice !== savedPreferences.voice ||
    areaCode !== (savedPreferences.areaCode ?? '')
  const savedConfigurationValid =
    query.data.completionReady &&
    (!savedPreferences.sms || smsEnabled) &&
    (!savedPreferences.voice || voiceEnabled) &&
    communicationsPatchSchema.safeParse({
      expectedRevision: activeRevision,
      ...savedPreferences,
    }).success

  function clearFeedback() {
    setFeedback(null)
  }

  async function save() {
    if (submissionStarted.current) return
    const nextErrors: Errors = {}
    const trimmedAreaCode = areaCode.trim()
    if (trimmedAreaCode && !/^[2-9][0-9]{2}$/.test(trimmedAreaCode)) {
      nextErrors.areaCode =
        'Enter a valid three-digit North American area code.'
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      setFeedback('invalid')
      return
    }

    const validation = communicationsPatchSchema.safeParse({
      expectedRevision: activeRevision,
      sms: smsEnabled ? sms : false,
      voice: voiceEnabled ? voice : false,
      ...(trimmedAreaCode ? { areaCode: trimmedAreaCode } : {}),
    })
    if (!validation.success) {
      setFeedback('invalid')
      return
    }

    submissionStarted.current = true
    setWorking(true)
    setFeedback(null)
    try {
      await client.saveCommunications(user, validation.data)
      const refreshed = await query.refetch()
      if (!refreshed.data) throw new Error('Communications setup unavailable')
      applyResponse(refreshed.data)
      await queryClient.invalidateQueries({
        queryKey: onboardingStatusQueryKey(user.uid),
      })
      setFeedback('saved')
    } catch (error) {
      if (
        error instanceof OnboardingApiError &&
        error.code === 'revision-conflict'
      ) {
        try {
          const refreshed = await query.refetch()
          if (refreshed.data) applyResponse(refreshed.data)
        } catch {
          // The bounded conflict message remains authoritative if refresh fails.
        } finally {
          setFeedback('conflict')
        }
      } else {
        setFeedback('error')
      }
    } finally {
      submissionStarted.current = false
      setWorking(false)
    }
  }

  async function complete() {
    if (submissionStarted.current || isDirty || !savedConfigurationValid) {
      return
    }
    submissionStarted.current = true
    setWorking(true)
    setFeedback(null)
    try {
      await client.completeCommunications(user, activeRevision)
      const status = await client.getStatus(user)
      queryClient.setQueryData(onboardingStatusQueryKey(user.uid), status)
      void navigate(destinationPath(routeOnboardingResponse(status)), {
        replace: true,
      })
    } catch (error) {
      if (
        error instanceof OnboardingApiError &&
        error.code === 'revision-conflict'
      ) {
        try {
          const refreshed = await query.refetch()
          if (refreshed.data) applyResponse(refreshed.data)
        } catch {
          // The conflict response remains bounded if refresh also fails.
        } finally {
          setFeedback('conflict')
        }
      } else {
        setFeedback('complete-error')
      }
    } finally {
      submissionStarted.current = false
      setWorking(false)
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <span className={styles.eyebrow}>Communications setup</span>
        <h1>Choose how patients can reach your clinic</h1>
        <p>
          Save your preferences now. Flio will assign and activate services in a
          separate, controlled setup step.
        </p>
      </header>

      <Card className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>Your Flio phone number</h2>
          <p>This is the single public number patients will use.</p>
        </div>
        {query.data.assignment.publicNumber ? (
          <output className={styles.publicNumber}>
            {formatPhoneNumber(query.data.assignment.publicNumber)}
          </output>
        ) : (
          <p className={styles.pendingNumber}>
            Your Flio phone number will be assigned during provisioning.
          </p>
        )}
        <p className={styles.assignmentStatus}>
          {assignmentStatusCopy(query.data.assignment.status)}
        </p>
      </Card>

      <Card className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>Text messaging</h2>
          <p>
            Flio can use your assigned number for patient text messaging after
            activation.
          </p>
        </div>
        <label className={styles.serviceOption}>
          <input
            type="checkbox"
            checked={smsEnabled && sms}
            disabled={!smsEnabled || working}
            onChange={(event) => {
              setSms(event.currentTarget.checked)
              clearFeedback()
            }}
          />
          <span>
            <strong>Enable text messaging preference</strong>
            {smsEnabled
              ? 'This saves your preference; it does not activate messaging.'
              : 'Text messaging is not included for this clinic.'}
          </span>
        </label>
        {smsEnabled && sms ? (
          <label className={styles.field}>
            <span>Preferred area code (optional)</span>
            <input
              inputMode="numeric"
              maxLength={3}
              value={areaCode}
              disabled={working}
              aria-invalid={Boolean(errors.areaCode)}
              onChange={(event) => {
                setAreaCode(event.currentTarget.value)
                setErrors((current) => ({ ...current, areaCode: undefined }))
                clearFeedback()
              }}
            />
            <small>
              We’ll try to assign a local number in this area code when
              available. Availability isn’t guaranteed.
            </small>
            {errors.areaCode ? (
              <small className={styles.error}>{errors.areaCode}</small>
            ) : null}
          </label>
        ) : null}
      </Card>

      <Card className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>Voice calls</h2>
          <p>
            Ava answers every call that reaches Flio, at any hour. During your
            staffed hours, a caller who asks for the front desk can be
            transferred to your Main phone from Clinic Info.
          </p>
        </div>
        <label className={styles.serviceOption}>
          <input
            type="checkbox"
            checked={voiceEnabled && voice}
            disabled={!voiceEnabled || working}
            onChange={(event) => {
              setVoice(event.currentTarget.checked)
              clearFeedback()
            }}
          />
          <span>
            <strong>Enable Voice calls preference</strong>
            {voiceEnabled
              ? 'This saves your preference; it does not activate Voice.'
              : 'Voice calls are not included for this clinic.'}
          </span>
        </label>
      </Card>

      <Card className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>Setup status</h2>
          <p>These details are managed by Flio and cannot be edited here.</p>
        </div>
        <dl className={styles.statusGrid}>
          <div>
            <dt>Text messaging registration</dt>
            <dd>{approvalCopy(query.data.smsApproval.status)}</dd>
          </div>
          <div>
            <dt>Service setup</dt>
            <dd>{provisioningCopy(query.data.provisioning.status)}</dd>
          </div>
          <div>
            <dt>Configuration review</dt>
            <dd>{statusLabel(query.data.validation.status)}</dd>
          </div>
        </dl>
      </Card>

      {feedback ? (
        <p
          className={`${styles.feedback} ${['error', 'conflict', 'invalid'].includes(feedback) ? styles.feedbackError : ''}`}
          role={feedback === 'saved' ? 'status' : 'alert'}
        >
          {feedback === 'saved'
            ? 'Preferences saved. Communications setup is still pending activation.'
            : feedback === 'conflict'
              ? 'Communications preferences changed in another session. The latest saved version has been restored; review it before saving again.'
              : feedback === 'invalid'
                ? 'Review the highlighted communications details before saving.'
                : feedback === 'complete-error'
                  ? 'Communications could not be completed. No setup state changed; review the saved preferences and try again.'
                  : 'Preferences could not be saved. Your entries are still here; try again.'}
        </p>
      ) : null}

      {isDirty ? (
        <p className={styles.note} role="status">
          Save your changes before continuing.
        </p>
      ) : !query.data.completionReady ? (
        <p className={styles.note} role="status">
          Save the current preferences before continuing.
        </p>
      ) : null}

      <div className={styles.actions}>
        <Button
          type="button"
          variant="ghost"
          disabled={working}
          onClick={() => void onSignOut()}
        >
          Sign out
        </Button>
        <Button type="button" disabled={working} onClick={() => void save()}>
          {working ? 'Saving…' : 'Save preferences'}
        </Button>
        <Button
          type="button"
          disabled={working || isDirty || !savedConfigurationValid}
          onClick={() => void complete()}
        >
          {working ? 'Working…' : 'Continue'}
        </Button>
      </div>
    </div>
  )
}
